<script lang="ts" setup>
// Wraps b-sidebar (dev-guidelines.md: never use b-xxx directly).
//
// - position="static" disables b-sidebar's own escape/outside-click close —
//   any click inside closes it on mobile instead (any tap = pick an item
//   and dismiss.
// - `modelValue` is the caller's manual open/closed intent for mobile only —
//   desktop always shows regardless of it, computed locally below, so there's
//   no need to emit a correction back whenever the breakpoint is crossed.
// - `mobileOnly` skips that desktop force-open, for callers with their own desktop nav (participant.vue).
import { useMediaQuery } from "@vueuse/core";

const props = defineProps<{
  modelValue: boolean;
  mobileOnly?: boolean;
  rail?: boolean;
}>();

const emit = defineEmits<{
  "update:modelValue": [open: boolean];
}>();

// 769 matches Bulma's $tablet below — keep both in sync if it changes.
const isDesktop = useMediaQuery("(min-width: 769px)");
const isOpen = computed(() =>
  props.mobileOnly ? props.modelValue : isDesktop.value || props.modelValue
);

const closeIfMobile = () => {
  if (!isDesktop.value) emit("update:modelValue", false);
};
</script>

<template>
  <b-sidebar
    :model-value="isOpen"
    class="ds-sidebar"
    position="static"
    fullheight
    :overlay="!isDesktop"
    :reduce="rail && isDesktop"
    :expand-on-hover="rail"
    :expand-on-hover-fixed="rail"
    @update:model-value="emit('update:modelValue', $event)"
    @click="closeIfMobile"
  >
    <slot />
  </b-sidebar>
</template>

<style scoped lang="scss">
:deep(.sidebar-content.is-static) {
  position: relative;
  z-index: 39;

  @media (width < 769px) {
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
  }
}

// width, not min-width: on mouse-out the sidebar is back in flow still 220px wide and would push the content.
.ds-sidebar:has(.sidebar-content.is-mini) {
  width: var(--bulma-sidebar-mobile-width);
}

:deep(.sidebar-content.is-mini) {
  overflow: hidden;
  white-space: nowrap;

  // .icon-text wraps the label under the icon at rail width.
  .menu-list a {
    flex-wrap: nowrap;
  }

  .menu-label,
  .menu-list a span:not(.icon) {
    transition: opacity 200ms ease;
  }

  &:not(:hover) {
    .menu-label,
    .menu-list a span:not(.icon) {
      opacity: 0;
    }
  }
}
</style>
